import { Box, Typography } from '@mui/material';
import React from 'react';
import StyledSection from '../components/styles/StyledSection';
import { StyledHeading } from '../components/styles/styles';
import ImageCarousel from '../components/template/ImageCarousel';
import LeftRightList from '../components/template/LeftRightList';
import StandardHeaderText from '../components/template/StandardHeaderText';
import Subtitle from '../components/template/Subtitle';
import Table from '../components/template/Table';
import TextBlock from '../components/template/TextBlock';
import HeroImage from '../components/template/HeroImage';
import { CmsBlock } from '../models/FundUpdateEntry';

/** A single dated CMS block ready to render in the read-only fund update layout. */
export interface FundUpdateReadEntry {
  date: string;
  /** Optional update title; nothing is shown when blank. */
  title?: string | null;
  blocks: CmsBlock[];
}

const noop = () => {};

/**
 * Edit callbacks every CMS block component requires; never invoked in read-only
 * mode. Also forces `openSimplified: false` — StandardHeaderText auto-scrolls to
 * itself and opens its editor on mount when that flag is set (meant for a block
 * just inserted in the live editor), which would otherwise fire for every block
 * rendered here regardless of what the saved data happens to carry.
 */
const READ_ONLY_HANDLERS = {
  editMode: false as const,
  openSimplified: false as const,
  onUpdate: noop,
  onRemove: noop,
  onMove: noop,
  onModifyActionButtons: noop,
};

/** Maps a CMS block type to the component that renders it. */
const BLOCK_COMPONENTS: Record<CmsBlock['type'], React.ComponentType<any>> = {
  StandardHeaderText,
  Subtitle,
  Text: TextBlock,
  HeroImage,
  ImageCarousel,
  LeftRightList,
  Table,
};

/**
 * Inside an update the update's own title is the primary heading, so each block's heading steps
 * down to a secondary size — otherwise every heading renders at the same size (the blocks are
 * shared with Fund Information, where their headings *are* the top level).
 */
const UPDATE_SECTION_HEADING_SX = { '& .heading-heading2': { fontSize: '1.125rem' } };

/** Read-only rendering of CMS blocks (no edit controls). Unknown types are skipped. */
export function ReadOnlyBlocks({ blocks }: { blocks: CmsBlock[] }) {
  return blocks
    .map((block) => {
      const Component = BLOCK_COMPONENTS[block.type];
      if (!Component) {
        return null;
      }
      return (
        <Box className="content-management-item" key={block.id} sx={UPDATE_SECTION_HEADING_SX}>
          <Component {...(block.properties as any)} {...READ_ONLY_HANDLERS} />
        </Box>
      );
    })
    .filter(Boolean);
}

export function formatUpdateDate(dateStr: string): string {
  try {
    return new Date(dateStr).toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

/**
 * Presentational read-only layout for fund updates shared by the investor web
 * view and the admin simplified editor's web preview, so the preview matches the
 * published web view exactly.
 */
export default function FundUpdatesReadLayout({ entries }: { entries: FundUpdateReadEntry[] }) {
  return (
    <StyledSection tone="tone1" sx={{ mt: '1.875rem' }}>
      <StyledHeading>Fund Updates</StyledHeading>
      {entries.map(({ date, title, blocks }, index) => (
        <Box key={`${date}-${index}`} sx={{ mb: 3 }}>
          <Typography
            variant="subtitle2"
            sx={{ color: 'text.secondary', mb: 1, fontStyle: 'italic' }}
          >
            {formatUpdateDate(date)}
          </Typography>
          {title?.trim() && (
            <StyledHeading
              headerVariant="heading2"
              component="h3"
              className="fund-update-title"
              sx={{ pt: 0, pb: 1 }}
            >
              {title.trim()}
            </StyledHeading>
          )}
          <ReadOnlyBlocks blocks={blocks} />
        </Box>
      ))}
    </StyledSection>
  );
}
